import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FileIcon, Loader2, PaperclipIcon, X, CloudUpload } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { useSession } from "@clerk/clerk-react";
import { getUnexpiredToken } from "@/utils/utils";
import type { UploadedFile } from "@/utils/types";
import {
  uploadFileToServer,
  type UploadFileResult,
} from "@/lib/file-upload.utils";
import type { SignedInSessionResource } from "@clerk/types";
import { useCanvasId } from "@/utils/utils";
import { useFileConflictModalStore } from "@/store/zustand/FileConflictModal";

interface ApiResponse {
  message?: string;
  url?: string;
  error?: string;
}

// Default configuration
const DEFAULT_MAX_FILE_SIZE_MB = 30;
const DEFAULT_MAX_FILES = 20;
export const DEFAULT_SUPPORTED_FILE_TYPES = [
  "application/pdf",
  // Only pdfs supported as non-image files for api
  // https://docs.anthropic.com/en/docs/build-with-claude/pdf-support
  // Maybe more in future? Their website allows other file types :s
  // https://support.anthropic.com/en/articles/8241126-what-kinds-of-documents-can-i-upload-to-claude-ai
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/bmp",
  "image/tiff",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
];

// Utility function to compress images for 64x64 preview when uploading to codebase
const compressImageForPreview = async (
  file: File,
  maxWidth = 64,
  maxHeight = 64
): Promise<{ base64: string; mimeType: string }> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Set canvas size to 64x64
      canvas.width = maxWidth;
      canvas.height = maxHeight;

      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      // Clear canvas with transparent background for PNG/SVG
      const hasTransparency =
        file.type === "image/png" || file.type === "image/svg+xml";
      if (!hasTransparency) {
        // Fill with white background for JPEG formats
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, maxWidth, maxHeight);
      }

      // Draw the image scaled down to 64x64
      ctx.drawImage(img, 0, 0, maxWidth, maxHeight);

      // Choose format based on original file type to preserve transparency
      const outputFormat = hasTransparency ? "image/png" : "image/jpeg";
      const quality = outputFormat === "image/png" ? undefined : 0.7; // PNG doesn't use quality parameter

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Could not compress image"));
            return;
          }

          const reader = new FileReader();
          reader.onload = () => {
            const base64 = reader.result as string;
            resolve({
              base64: base64.split(",")[1], // Return base64 without data URL prefix
              mimeType: outputFormat,
            });
          };
          reader.onerror = () =>
            reject(new Error("Could not read compressed image"));
          reader.readAsDataURL(blob);
        },
        outputFormat,
        quality
      );
    };

    img.onerror = () => reject(new Error("Could not load image"));
    img.src = URL.createObjectURL(file);
  });
};

type UseImageUploaderOptions = {
  maxFileSizeMb?: number;
  maxFiles?: number;
  acceptedMimeTypes?: string[];
  onUploadSuccess?: (file: UploadedFile) => void;

  // Options for codebase uploads
  forCodebase?: boolean;
  codebaseDirectory?: string;
};

// Shared utility functions for handling paste and file operations
export const createHandleImagePaste = (
  setPastedFiles: (files: File[]) => void
) => {
  return (
    event: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>
  ) => {
    if (event.defaultPrevented) return;

    const items = event.clipboardData.items;
    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      // Accept all file types, not just images
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      setPastedFiles(files);
      return;
    }
  };
};

export const createHandlePastedFilesAsContext = (
  uploadFiles: (files: File[]) => Promise<void>,
  setPastedFiles: (files: File[]) => void
) => {
  return async (files: File[]) => {
    try {
      await uploadFiles(files);
      setPastedFiles([]);
    } catch (error) {
      console.error("Error uploading pasted files as context:", error);
    }
  };
};

export const createHandlePastedFilesToCodebase = (
  uploadFiles: (
    files: File[],
    overrides?: {
      forCodebase?: boolean;
      canvasId?: string;
      codebaseDirectory?: string;
      onUploadSuccess?: (file: UploadedFile) => void;
    }
  ) => Promise<void>,
  setPastedFiles: (files: File[]) => void,
  canvasId?: string,
  codebaseDirectory?: string
) => {
  return async (files: File[]) => {
    try {
      await uploadFiles(files, {
        forCodebase: true,
        canvasId,
        codebaseDirectory,
      });
      setPastedFiles([]);
    } catch (error) {
      console.error("Error uploading pasted files to codebase:", error);
    }
  };
};

export const createIsContextUploadSupported = () => {
  return (files: File[]) => {
    return files.every((file) =>
      DEFAULT_SUPPORTED_FILE_TYPES.includes(file.type)
    );
  };
};

export const useDirectContextUpload = (
  uploadFiles: (files: File[]) => Promise<void>
) => {
  const handleDirectPaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>) => {
      if (event.defaultPrevented) return;

      const items = Array.from(event.clipboardData?.items || []);
      const files = items
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);

      if (files.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        // Upload files immediately as context
        uploadFiles(files);
      }
    },
    [uploadFiles]
  );

  return { handleDirectPaste };
};

type UseImageUploaderReturnType = Readonly<{
  uploadFiles: (
    files: File[],
    overrides?: {
      forCodebase?: boolean;
      canvasId?: string;
      codebaseDirectory?: string;
      onUploadSuccess?: (file: UploadedFile) => void;
    }
  ) => Promise<void>;
  uploadedFiles: UploadedFile[];
  isUploading: boolean;
  triggerUpload: () => void;
  clearUploadedFiles: () => void;
  FilePreviewerComponent: () => JSX.Element | null;
  FileUploadComponent: ({
    className,
    fileUploadCallback,
    buttonLabel,
    layout,
  }: {
    className?: string;
    fileUploadCallback?: () => void;
    buttonLabel?: React.ReactNode;
    layout?: "row" | "column";
  }) => JSX.Element;
  FilePreviewItem: ({
    file,
    onRemove,
  }: {
    file: UploadedFile;
    onRemove?: () => void;
  }) => JSX.Element;
  FileUploadToCodebaseComponent: ({
    codebaseDirectory,
    onUploadSuccess,
    className,
    buttonLabel,
    fileUploadCallback,
    layout,
  }: {
    codebaseDirectory?: string;
    onUploadSuccess?: (file: UploadedFile) => void;
    className?: string;
    buttonLabel?: React.ReactNode;
    fileUploadCallback?: () => void;
    layout?: "row" | "column";
  }) => JSX.Element;
  // Shared utility functions
  handleImagePaste: (
    event: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>
  ) => void;
  handlePastedFilesAsContext: (files: File[]) => Promise<void>;
  handlePastedFilesToCodebase: (files: File[]) => Promise<void>;
  isContextUploadSupported: (files: File[]) => boolean;
  pastedFiles: File[];
  setPastedFiles: (files: File[]) => void;
}>;

export function useImageUploader(
  options?: UseImageUploaderOptions
): UseImageUploaderReturnType {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pastedFiles, setPastedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasId = useCanvasId();
  const uploadCancelledRef = useRef(false);

  const { session } = useSession();

  // Listen to file conflict modal state to detect cancellation
  const modalIsOpen = useFileConflictModalStore((state) => state.isOpen);
  const prevModalIsOpenRef = useRef(modalIsOpen);

  // Effect to detect when modal closes (potential cancellation)
  useEffect(() => {
    if (prevModalIsOpenRef.current && !modalIsOpen && isUploading) {
      // Modal was open and now closed while uploading - likely cancelled
      setIsUploading(false);
      uploadCancelledRef.current = true;
    }
    prevModalIsOpenRef.current = modalIsOpen;
  }, [modalIsOpen, isUploading]);

  const maxFileSizeMb = options?.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
  const maxFiles = options?.maxFiles ?? DEFAULT_MAX_FILES;
  const supportedFileTypes =
    options?.acceptedMimeTypes ?? DEFAULT_SUPPORTED_FILE_TYPES;
  const onUploadSuccessCallback = options?.onUploadSuccess;

  const forCodebase = options?.forCodebase ?? false;
  const codebaseDirectory = options?.codebaseDirectory;

  const actualSupportedFileTypes = forCodebase
    ? [] // No client-side MIME type restrictions for codebase uploads
    : supportedFileTypes;

  // Function to trigger file input click
  const triggerUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadFiles = async (
    files: File[],
    overrides?: {
      forCodebase?: boolean;
      canvasId?: string;
      codebaseDirectory?: string;
      onUploadSuccess?: (file: UploadedFile) => void;
    }
  ) => {
    // Use overrides or fall back to hook options
    const isForCodebase = overrides?.forCodebase ?? forCodebase;
    const uploadCanvasId = overrides?.canvasId ?? canvasId;
    const uploadCodebaseDirectory =
      overrides?.codebaseDirectory ?? codebaseDirectory;
    const uploadSuccessCallback =
      overrides?.onUploadSuccess ?? onUploadSuccessCallback;

    // Check max files based on config
    if (uploadedFiles.length + files.length > maxFiles) {
      toast.error(
        `Maximum ${maxFiles} file${maxFiles !== 1 ? "s" : ""} allowed`
      );
      return;
    }

    setIsUploading(true);

    const successfulUploads: UploadedFile[] = [];

    try {
      const filesToAttemptUpload = files.filter((file) => {
        // Client-side MIME check only if not forCodebase
        if (!isForCodebase) {
          if (
            actualSupportedFileTypes.length > 0 &&
            !actualSupportedFileTypes.includes(file.type)
          ) {
            toast.warning(
              `File type not supported for AI context: ${file.name}. Supported types: PDF, images.`
            );
            return false;
          }
        }
        // Size check applies to all
        if (file.size > maxFileSizeMb * 1024 * 1024) {
          toast.error(`File ${file.name} exceeds ${maxFileSizeMb}MB limit`);
          return false;
        }
        return true;
      });

      if (filesToAttemptUpload.length === 0 && files.length > 0) {
        return;
      }

      for (const file of filesToAttemptUpload) {
        try {
          if (isForCodebase) {
            if (!session) {
              toast.error(
                "User session is not available for codebase file upload."
              );
              console.error(
                "User session is not available for codebase file upload."
              );
              continue;
            }
            if (!uploadCanvasId) {
              toast.error(`Cannot upload ${file.name}: Canvas ID missing.`);
              console.error(`Cannot upload ${file.name}: Canvas ID missing.`);
              continue;
            }

            const result: UploadFileResult = await uploadFileToServer({
              file,
              session: session as SignedInSessionResource,
              canvasId: uploadCanvasId,
              forUploadToCodebase: true,
              codebaseDirectory: uploadCodebaseDirectory,
            });

            let previewUrl = result.filePath;

            // For images, also upload a compressed version for preview
            if (file.type.startsWith("image/")) {
              try {
                const compressed = await compressImageForPreview(file);
                const supabaseJWT = await getUnexpiredToken(session);

                const response = await fetch(
                  `${import.meta.env.VITE_BACKEND_BASE_URL}/imageUpload`,
                  {
                    method: "POST",
                    body: JSON.stringify({
                      file: compressed.base64,
                      fileName: `preview_${file.name}`,
                      mimeType: compressed.mimeType,
                    }),
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${supabaseJWT}`,
                    },
                  }
                );

                const data = (await response.json()) as ApiResponse;
                if (response.ok && data.url && !data.error) {
                  previewUrl = data.url;
                }
              } catch (error) {
                console.warn("Failed to upload compressed preview:", error);
                // Continue with original file path as preview URL
              }
            }

            const newFile: UploadedFile = {
              url: previewUrl,
              name: result.fileName,
              type: file.type.startsWith("image/") ? "image" : "file",
              mimeType: file.type,
              pathInCodebase: result.filePath, // Store the codebase path separately
            };
            successfulUploads.push(newFile);
            if (uploadSuccessCallback) {
              uploadSuccessCallback(newFile);
            }
          } else {
            // Existing logic for base64 /imageUpload
            const reader = new FileReader();
            reader.readAsDataURL(file);

            const base64data = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
            });

            const supabaseJWT = await getUnexpiredToken(session);

            const response = await fetch(
              `${import.meta.env.VITE_BACKEND_BASE_URL}/imageUpload`,
              {
                method: "POST",
                body: JSON.stringify({
                  file: base64data.split(",")[1],
                  fileName: file.name,
                  mimeType: file.type,
                }),
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${supabaseJWT}`,
                },
              }
            );
            const data = (await response.json()) as ApiResponse;

            if (!response.ok || data.error) {
              toast.error(data.error || `Failed to upload ${file.name}`);
              // Removed 'continue' here to match original logic flow, error implies upload failed for this file.
            }

            if (data.url) {
              const fileType = file.type.startsWith("image/")
                ? "image"
                : "file";
              const newFile: UploadedFile = {
                url: data.url,
                name: file.name,
                type: fileType,
                mimeType: file.type,
              };
              successfulUploads.push(newFile);
              if (uploadSuccessCallback) {
                uploadSuccessCallback(newFile);
              }
            } else if (!data.error) {
              // Only throw if no explicit error message was already handled
              throw new Error(
                `Image URL not received after uploading ${file.name}.`
              );
            }
          }
        } catch (error) {
          console.error("Inner catch block error:", error);
          const errorMessage =
            (error as Error).message || `Error uploading ${file.name}`;

          // If user cancelled the upload via conflict modal, stop the entire upload process
          if (errorMessage === "File upload cancelled by user") {
            throw error; // Re-throw to be caught by outer try-catch
          }

          toast.error(errorMessage);
        }
      }

      if (successfulUploads.length > 0) {
        setUploadedFiles((prev) => [...prev, ...successfulUploads]);
      }
    } catch (outerError) {
      console.error("Outer catch block error:", outerError);
      // Don't show toast for cancellation errors as they're user-initiated
      if ((outerError as Error).message !== "File upload cancelled by user") {
        toast.error((outerError as Error).message || "Upload failed");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const clearUploadedFiles = () => {
    // Clean up any blob URLs before clearing
    for (const file of uploadedFiles) {
      if (file.url.startsWith("blob:")) {
        URL.revokeObjectURL(file.url);
      }
    }
    setUploadedFiles([]);
  };

  // Create shared utility functions using the hook's state and methods
  const handleImagePaste = useCallback(
    createHandleImagePaste(setPastedFiles),
    []
  );

  const handlePastedFilesAsContext = useCallback(
    createHandlePastedFilesAsContext(uploadFiles, setPastedFiles),
    []
  );

  const handlePastedFilesToCodebase = useCallback(
    createHandlePastedFilesToCodebase(
      uploadFiles,
      setPastedFiles,
      canvasId,
      codebaseDirectory
    ),
    []
  );

  const isContextUploadSupported = useCallback(
    createIsContextUploadSupported(),
    []
  );

  const deleteCodebaseFile = async (
    file: UploadedFile,
    canvasIdToUse?: string
  ): Promise<boolean> => {
    if (!file.pathInCodebase || !session || isLoading) {
      return false;
    }

    setIsLoading(true);
    try {
      // Use the passed canvasId or try to get it from context
      const canvasIdForDeletion = canvasIdToUse || canvasId;

      if (!canvasIdForDeletion) {
        console.error("Cannot delete file: Canvas ID missing");
        return false;
      }

      // Use the existing removeFile endpoint
      const supabaseJWT = await getUnexpiredToken(session);
      const response = await fetch(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasIdForDeletion}/removeFile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabaseJWT}`,
          },
          body: JSON.stringify({
            filename: file.pathInCodebase, // Use the pathInCodebase instead of URL
            isDirectory: false,
          }),
        }
      );

      if (!response.ok) {
        console.error(`Failed to delete file: ${response.statusText}`);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error deleting file from codebase:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const FilePreviewItem = ({
    file,
    onRemove,
  }: {
    file: UploadedFile;
    onRemove: () => void;
  }) => {
    return (
      <div className="relative w-fit rounded-md  mt-2">
        {onRemove && (
          <X
            className={cn(
              "absolute cursor-pointer -top-1 -right-1.5 z-10 w-5 h-5 hover:text-icons-primary text-icons-secondary bg-background-secondary hover:bg-background-accent rounded-md p-0.5 size-5",
              isLoading &&
                "opacity-50 cursor-not-allowed hover:text-icons-secondary hover:bg-background-secondary"
            )}
            onClick={isLoading ? undefined : onRemove}
          />
        )}
        <div
          className={cn(
            "h-16 w-16 rounded-md relative flex items-center justify-center p-1",
            file.type === "image" ? "checkerboard" : "bg-background-secondary"
          )}
          style={
            file.type === "image"
              ? {
                  backgroundImage: `
              linear-gradient(45deg, var(--background-tertiary) 25%, transparent 25%), 
              linear-gradient(-45deg, var(--background-tertiary) 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, var(--background-tertiary) 75%), 
              linear-gradient(-45deg, transparent 75%, var(--background-tertiary) 75%)
            `,
                  backgroundSize: "8px 8px",
                  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
                  backgroundColor: "bg-background-secondary",
                }
              : undefined
          }
        >
          {file.type === "image" ? (
            <img
              src={file.url}
              alt="Uploaded"
              height={100}
              width={100}
              className="h-10 w-16 rounded-sm relative flex items-center justify-center object-contain"
            />
          ) : file.mimeType === "application/pdf" ? (
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-full"
            >
              <div className="flex flex-col items-center justify-center w-full h-full">
                <FileIcon className="size-4 text-icons-primary" />
                <span className="text-xs truncate max-w-full">PDF</span>
              </div>
            </a>
          ) : (
            <div className="flex flex-col items-center justify-center w-full h-full">
              <FileIcon className="size-4 text-icons-primary" />
              <span className="text-xs truncate max-w-full">File</span>
            </div>
          )}
          {file.pathInCodebase && (
            <CloudUpload className="text-icons-secondary absolute bottom-1 left-1 z-10 size-4" />
          )}
        </div>
      </div>
    );
  };

  const FilePreviewerComponent = useCallback(() => {
    if (!uploadedFiles.length && !isUploading) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-2">
        {uploadedFiles.map((file, index) => (
          <FilePreviewItem
            key={`${file.name}-${index}`}
            file={file}
            onRemove={async () => {
              if (isLoading) return;
              const originalFiles = uploadedFiles;
              setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

              let success = false;
              if (file.pathInCodebase) {
                try {
                  success = await deleteCodebaseFile(file);
                } catch (error) {
                  // If delete fails, restore the file to the state
                  success = false;
                }

                if (!success) {
                  setUploadedFiles(originalFiles);
                  toast.error(`Failed to delete ${file.name} from codebase`);
                }
              } else {
                // Clean up blob URL if it exists
                if (file.url.startsWith("blob:")) {
                  URL.revokeObjectURL(file.url);
                }
              }
            }}
          />
        ))}
        {isUploading && (
          <div className="mt-2 h-16 w-16 bg-background-secondary rounded-sm flex items-center justify-center">
            <Loader2 className="animate-spin text-icons-primary" />
          </div>
        )}
      </div>
    );
  }, [
    isUploading,
    uploadedFiles,
    FilePreviewItem,
    deleteCodebaseFile,
    isLoading,
  ]);

  const FileUploadComponent = useCallback(
    ({
      className,
      fileUploadCallback,
      buttonLabel,
      layout = "row",
    }: {
      className?: string;
      fileUploadCallback?: () => void;
      buttonLabel?: React.ReactNode;
      layout?: "row" | "column";
    }) => {
      const uploadClick = () => triggerUpload(); // Use the exposed trigger

      const disabled = isUploading;

      function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
          uploadFiles(files);
          fileUploadCallback?.();
        }
        if (e.target) {
          e.target.value = "";
        }
      }

      return (
        <>
          <Button
            variant="ghost"
            className={cn(
              "h-full flex py-1 px-2 items-center rounded group text-xs text-secondary-n-50 w-full min-h-0",
              layout === "column"
                ? "flex-col justify-center items-center gap-1"
                : "gap-2 justify-start items-center",
              className
            )}
            disabled={disabled}
            onClick={uploadClick}
          >
            <PaperclipIcon
              className={cn(
                "cursor-pointer size-4 text-icons-muted group-hover:text-icons-primary outline-none flex-shrink-0",
                disabled && "cursor-default opacity-75"
              )}
              data-tooltip-content={disabled ? "" : "Upload files"}
              data-tooltip-id="tooltip"
            />
            <span className="leading-none">
              {buttonLabel || "Upload context for AI"}
            </span>
          </Button>
          <input
            type="file"
            multiple={maxFiles !== 1} // Set multiple based on config
            ref={fileInputRef}
            className="hidden"
            disabled={disabled}
            onChange={handleFileChange}
            accept={
              forCodebase
                ? "*"
                : actualSupportedFileTypes.length > 0
                ? actualSupportedFileTypes.join(",")
                : "*"
            }
          />
        </>
      );
    },
    [
      isUploading,
      triggerUpload,
      maxFiles,
      actualSupportedFileTypes,
      forCodebase,
    ]
  );

  const FileUploadToCodebaseComponent = ({
    codebaseDirectory,
    onUploadSuccess,
    className,
    buttonLabel,
    fileUploadCallback,
    layout = "row",
  }: {
    codebaseDirectory?: string;
    onUploadSuccess?: (file: UploadedFile) => void;
    className?: string;
    buttonLabel?: React.ReactNode;
    fileUploadCallback?: () => void;
    layout?: "row" | "column";
  }) => {
    const codebaseFileInputRef = useRef<HTMLInputElement>(null);
    const actualButtonLabel = buttonLabel || "Upload to Codebase";

    const handleCodebaseFileUpload = async (files: File[]) => {
      await uploadFiles(files, {
        forCodebase: true,
        canvasId,
        codebaseDirectory,
        onUploadSuccess,
      });
      fileUploadCallback?.();
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        handleCodebaseFileUpload(files);
      }
      if (e.target) {
        e.target.value = "";
      }
    };
    return (
      <>
        <Button
          variant="ghost"
          className={cn(
            "h-full flex py-1 px-2 w-full items-center rounded group text-xs text-secondary-n-50 min-h-0",
            layout === "column"
              ? "flex-col justify-center items-center gap-1"
              : "gap-2 justify-start items-center",
            className
          )}
          disabled={isUploading}
          onClick={() => codebaseFileInputRef.current?.click()}
        >
          <PaperclipIcon
            className={cn(
              "cursor-pointer size-4 text-icons-muted group-hover:text-icons-primary outline-none flex-shrink-0",
              isUploading && "cursor-default opacity-75"
            )}
            data-tooltip-content={isUploading ? "" : "Upload files to codebase"}
            data-tooltip-id="tooltip"
          />
          <span className="leading-none">{actualButtonLabel}</span>
        </Button>
        <input
          type="file"
          multiple
          ref={codebaseFileInputRef}
          className="hidden"
          disabled={isUploading}
          onChange={handleFileChange}
          accept="*" // Accept all file types for codebase uploads
        />
      </>
    );
  };

  return {
    uploadFiles,
    uploadedFiles,
    isUploading,
    triggerUpload,
    clearUploadedFiles,
    FilePreviewerComponent,
    FileUploadComponent,
    FilePreviewItem,
    FileUploadToCodebaseComponent,
    // Shared utility functions
    handleImagePaste,
    handlePastedFilesAsContext,
    handlePastedFilesToCodebase,
    isContextUploadSupported,
    pastedFiles,
    setPastedFiles,
  };
}
